import { useQuery, UseQueryResult } from 'react-query';
import moment from 'moment';
import { get } from '../utils';

/**
 * Everything the Overview page shows, as aggregates.
 *
 * The page used to fetch every ledger row, every organisation, every customer and every
 * device assignment of the current year and count them in the browser — ~271,000 rows and
 * tens of megabytes on production data, for four integers and a thirteen-month chart.
 */

export interface OverviewCreatedCounts {
  tetherTicks: number;
  customers: number;
  organisations: number;
  deviceAssignments: number;
}

export interface OverviewTickSeries {
  customerId: string;
  customerName: string;
  /** One count per month in {@link OverviewStats.months}, same order. */
  counts: number[];
}

export interface OverviewStats {
  createdSince: {
    day: OverviewCreatedCounts;
    month: OverviewCreatedCounts;
    year: OverviewCreatedCounts;
  };
  /** `YYYY-MM`, oldest first. */
  months: string[];
  tetherTicksByCustomer: OverviewTickSeries[];
}

/** Months of history the chart shows, including the current one. Matches the API. */
export const OVERVIEW_CHART_MONTHS = 13;

export function getOverviewStats(): Promise<OverviewStats> {
  const monthStart = moment().startOf('month');
  return get<OverviewStats>('overview', {
    // The window boundaries are computed HERE, in the browser's timezone, and sent — the
    // API runs in UTC, so deriving them server-side would move every counter by up to 13
    // hours for a New Zealand operator. On the "today" counter that is most of the day.
    dayStart: moment().startOf('day').format(),
    monthStart: monthStart.format(),
    yearStart: moment().startOf('year').format(),
    chartFrom: monthStart.clone().subtract(OVERVIEW_CHART_MONTHS - 1, 'months').format(),
    // Which month that instant IS, in this browser's calendar. The instant alone cannot
    // say: an offset-bearing string and its UTC normalisation are the same moment, and the
    // month recoverable from the second one is the wrong month for every operator east of
    // UTC. Sent explicitly so the axis cannot silently lose the current month.
    chartMonth: monthStart.clone().subtract(OVERVIEW_CHART_MONTHS - 1, 'months').format('YYYY-MM'),
  });
}

export function useGetOverviewStats(): UseQueryResult<OverviewStats, Error> {
  // The local day is part of the KEY, not just of the request. The boundaries are computed
  // inside the query function and then frozen in the cached response, and this is a
  // landing page people leave open: at 00:05 the "created today" radial would still be
  // showing yesterday's totals against today's target, with no interval, no focus refetch
  // and no remount to correct it. Crossing midnight now moves to a different key.
  const today = moment().format('YYYY-MM-DD');
  return useQuery<OverviewStats, Error>(['OverviewStats', today], () => getOverviewStats(), {
    // The counters move slowly and the page is a landing screen people leave open.
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    // And because it is left open, it has to re-ask on its own: the key above only
    // recomputes on a render, so a tab nobody touches would sit on yesterday's "today"
    // indefinitely. One aggregate request every ten minutes, against the four unbounded
    // list calls per page load this endpoint replaced.
    refetchInterval: 10 * 60_000,
    // In the BACKGROUND too. react-query pauses intervals for a hidden tab by default, so
    // without this the self-correction only starts once someone looks at the page — which
    // is the one thing an ops screen left open overnight does not get.
    refetchIntervalInBackground: true,
  });
}
